import React, { useState } from 'react';
import { keccak256, toBytes } from 'viem';
import { LogOut, Wrench, RefreshCw, CheckCircle2 } from 'lucide-react';
import { buildExitRecord, markFixed, closeWithRecheck } from '@/lib/agentExitRecord';
import { recheckPromise } from '@/lib/agentReady';

export function ExitRecordPanel({ promiseId, vendorKey, failedEvidence }) {
  const [rec, setRec] = useState(null);
  const make = async () => {
    const draft = buildExitRecord(promiseId, keccak256(toBytes(`api.example|exit|${promiseId}|${Date.now()}`)));
    const receivedAt = new Date().toISOString().slice(0, 19) + 'Z';
    const signature = await vendorKey.signMessage({ message: `${draft.recordId}|${receivedAt}` });
    setRec({ ...draft, receipt: { receivedAt, signedBy: 'api.example', signature }, status: 'seen' });
  };
  const fix = async () => {
    const fixed = markFixed(rec);
    setRec(fixed);
    const r = await recheckPromise(promiseId);
    setRec(closeWithRecheck(fixed, r));
  };
  if (!rec) {
    return (
      <button type="button" onClick={make} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-violet-400/50 px-3 py-1 text-xs font-semibold text-violet-200 hover:bg-violet-500/10 transition-colors" data-testid={`ar-exit-btn-${promiseId}`}>
        <LogOut className="w-3.5 h-3.5" /> Turn into an exit record
      </button>
    );
  }
  return (
    <div className="mt-2 rounded-lg border border-violet-400/30 bg-violet-400/[0.04] p-2.5" data-testid={`ar-exit-record-${promiseId}`}>
      <div className="text-xs text-violet-100/90 mb-1.5">
        Exit record sent to the vendor's claims endpoint. Status: <span className="font-mono text-amber-300" data-testid={`ar-exit-status-${promiseId}`}>{rec.status}</span>. The receipt is signed, so the agent can show it was delivered.
      </div>
      <pre className="font-mono text-[10.5px] text-gray-300 whitespace-pre-wrap break-all max-h-72 overflow-auto" data-testid={`ar-exit-json-${promiseId}`}>{JSON.stringify(rec, null, 2)}</pre>
      {rec.status === 'seen' && (
        <button type="button" onClick={fix} className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-400/50 px-3 py-1 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/10 transition-colors" data-testid={`ar-exit-fix-btn-${promiseId}`}>
          <Wrench className="w-3.5 h-3.5" /> Vendor replies and marks it fixed
        </button>
      )}
      {rec.vendorReply && <div className="mt-2 rounded-md border border-emerald-400/30 bg-emerald-400/[0.05] p-2 text-xs text-emerald-100/90" data-testid={`ar-exit-reply-${promiseId}`}>Vendor reply: {rec.vendorReply.text}</div>}
      {rec.status === 'fixed' && <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-200" data-testid={`ar-exit-rechecking-${promiseId}`}><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Fixed is a claim. Re-running this check to prove it...</div>}
      {rec.recheck && (
        <div className={`mt-2 rounded-md border p-2 text-xs ${rec.recheck.passed ? 'border-emerald-400/40 text-emerald-100/90' : 'border-red-400/40 text-red-100/90'}`} data-testid={`ar-exit-recheck-${promiseId}`}>
          <div className="flex items-center gap-1.5 font-semibold mb-0.5"><CheckCircle2 className="w-3.5 h-3.5" /> Re-check {rec.recheck.passed ? 'passed. Status: closed' : 'failed. Status: disputed'}</div>
          <div className="text-gray-400 line-through" data-testid={`ar-exit-recheck-before-${promiseId}`}>Before: {failedEvidence}</div>
          <div data-testid={`ar-exit-recheck-after-${promiseId}`}>Now: {rec.recheck.evidence}</div>
        </div>
      )}
      <div className="text-[11px] text-gray-500 mt-1.5">The record names no other vendor and carries no personal data. Kojiru never sees it and decides nothing.</div>
    </div>
  );
}
